/**
 * The import pipeline's contract — Phase 6 plan §2 U72.
 *
 * `apps/api`, `apps/worker` and `apps/app` all read these words, so each is spelled here once.
 * `drizzle/0040_imports` holds the status, source and action lists as CHECKs, set-equal to the
 * constants below by `constraints.test.ts`.
 */
import { z } from "zod";
/** The queue the import's steps run on. `drizzle/0040` creates it, by the owner. */
export const IMPORT_QUEUE = "import.step";
export const IMPORT_STATUSES = [
    "uploaded",
    "parsing",
    "mapping",
    "validating",
    "review",
    "applying",
    "done",
    "failed",
    "cancelled",
];
/** `shopify` has no writer until Phase 8; the CHECK carries it so that phase needs no migration. */
export const IMPORT_SOURCES = ["csv", "xlsx", "shopify"];
export const IMPORT_ACTIONS = ["create", "update", "skip", "error"];
/** What `validateRow` and the duplicate pass produce. No column holds a CHECK over these (Drift §U72). */
export const IMPORT_ERROR_KINDS = [
    "check_digit",
    "outside_prefix",
    "excel_mangled",
    "unreadable_date",
    "duplicate_gtin",
    "invalid_lot",
    "invalid_language",
    "unknown_attribute",
    "attribute_above_tier",
    "invalid_value",
];
export const importRowErrorSchema = z.object({
    kind: z.enum(IMPORT_ERROR_KINDS),
    detail: z.string(),
});
/** The six keys `attribute_defs` seeds in `0009`; set-equal to that table by `import-schema.test.ts`. */
export const IMPORT_ATTRIBUTE_KEYS = [
    "gs1:allergenStatement",
    "gs1:ingredientStatement",
    "gs1:nutrientBasisQuantity",
    "gs1:netContent",
    "gs1:countryOfOrigin",
    "gs1:storageHandling",
];
/**
 * Q-P17: one row is one product and at most one batch. A GTIN that appears twice is
 * `duplicate_gtin` on both rows. Variants, multi-lot rows, links and media are out.
 */
export const IMPORT_PRODUCT_FIELDS = [
    "gtin14",
    "brandName",
    "sku",
    "gpcCategory",
    "netContent",
];
export const IMPORT_LANG_FIELDS = ["name", "description"];
export const IMPORT_BATCH_FIELDS = [
    "batch.lot",
    "batch.expiry",
    "batch.mfgDate",
];
/** The fields a column may be mapped to, as a registry. Language fields are expanded per tag by the mapper. */
export const IMPORT_FIELD_REGISTRY = [
    { field: "gtin14", group: "product", required: "always" },
    { field: "brandName", group: "product", required: "on_create" },
    { field: "sku", group: "product", required: "no" },
    { field: "gpcCategory", group: "product", required: "no" },
    { field: "netContent", group: "product", required: "no" },
    ...IMPORT_LANG_FIELDS.map((field) => ({
        field,
        group: "language",
        required: "no",
    })),
    ...IMPORT_ATTRIBUTE_KEYS.map((field) => ({
        field,
        group: "attribute",
        required: "no",
    })),
    ...IMPORT_BATCH_FIELDS.map((field) => ({
        field,
        group: "batch",
        required: "no",
    })),
];
/**
 * How an ambiguous `03/04/27` is read. Stored on `imports.mapping`; `null` is unset, and unset
 * plus an ambiguous value is `unreadable_date`. `import_templates.date_order` holds no CHECK,
 * so the mapping door is the only validator. Drift §U75.
 */
export const DATE_ORDERS = ["dmy", "mdy", "ymd"];
/** `imports.mapping` and `import_templates.mapping`: source column header to a registry field. */
export const importMappingSchema = z.object({
    columns: z.array(z.object({
        header: z.string().min(1),
        /** `null` is a column the user chose not to import. */
        field: z.string().min(1).nullable(),
    })),
    /** How this file reads `03/04/27`. The door validates it; `null` and absent both mean unset. */
    dateOrder: z.enum(DATE_ORDERS).nullable().optional(),
});
/** Q-P31: a file with more data rows than this is refused; the screen renders the same figure. */
export const IMPORT_MAX_ROWS = 50_000;
/** One step per swept status: `parsing`, `validating`, `applying`. U74 builds the first. */
export const IMPORT_STEPS = ["parse", "validate", "apply"];
/**
 * What the apply door stores in `imports.checkpoint.apply`, in the transaction that sets
 * `applying`. The job payload holds only the import and the organisation, and a create needs
 * a workspace, an audit row needs an actor and the done mail needs a recipient (Drift §U77).
 */
export const importApplyRequestSchema = z.object({
    workspaceId: z.uuid(),
    limit: z.number().int().positive().nullable(),
    actorUserId: z.uuid(),
});
/** What a step job carries. The organisation travels with it so the worker scopes its own transaction. */
export const importStepJobSchema = z.object({
    importId: z.uuid(),
    orgId: z.uuid(),
    step: z.enum(IMPORT_STEPS),
});
//# sourceMappingURL=import.js.map